import useResizeObserver from 'hooks/useResizeObserver';
import { useEffect, useLayoutEffect, useState } from 'react';

const DRAWER_BODY_SELECTOR = '.top-menu-drawer .ant-drawer-body';
const SEASON_PANEL_SELECTOR = '#season-panel';
const COLLAPSE_HEADER_SELECTOR = '.ant-collapse-header';

const measureAvailableHeight = (body, collapseItemCount) => {
  if (!body || collapseItemCount <= 0) return 0;

  const seasonPanel = body.querySelector(SEASON_PANEL_SELECTOR);
  const headers = body.querySelectorAll(COLLAPSE_HEADER_SELECTOR);
  const seasonPanelHeight = seasonPanel?.offsetHeight ?? 0;
  const headersHeight = Array.from(headers).reduce((sum, header) => sum + header.offsetHeight, 0);

  // Body is the slot above the footer. Subtract the season panel and the real
  // collapse headers so the open panel fills the remainder instead of pushing
  // the other blocks down.
  return Math.max(0, body.clientHeight - seasonPanelHeight - headersHeight);
};

const useDrawerHeight = (filterDrawerOpen, collapseItemCount) => {
  const [setNode, entry] = useResizeObserver();
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (!filterDrawerOpen) return () => {};

    // Drawer renders via portal — the body element may not exist yet.
    // Use MutationObserver to reliably detect when it appears in the DOM.
    const element = document.querySelector(DRAWER_BODY_SELECTOR);
    if (!element) {
      const observer = new MutationObserver(() => {
        const found = document.querySelector(DRAWER_BODY_SELECTOR);
        if (!found) return;

        setNode(found);
        observer.disconnect();
      });

      observer.observe(document.body, { childList: true, subtree: true });

      return () => observer.disconnect();
    }

    setNode(element);
    return () => {};
  }, [filterDrawerOpen, setNode]);

  useLayoutEffect(() => {
    const body = entry?.target;
    if (!filterDrawerOpen || !body) return () => {};

    const updateHeight = () => {
      const nextHeight = measureAvailableHeight(body, collapseItemCount);
      setHeight((currentHeight) => (currentHeight === nextHeight ? currentHeight : nextHeight));
    };

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(body);
    const seasonPanel = body.querySelector(SEASON_PANEL_SELECTOR);
    if (seasonPanel) resizeObserver.observe(seasonPanel);

    for (const header of body.querySelectorAll(COLLAPSE_HEADER_SELECTOR)) {
      resizeObserver.observe(header);
    }

    return () => resizeObserver.disconnect();
  }, [entry, filterDrawerOpen, collapseItemCount]);

  return filterDrawerOpen ? height : 0;
};

export default useDrawerHeight;
